/**
 * VistoeraTASK-00546 — Platform Admin「租户管理 → 租户列表」: Team state,
 * entitlement source, member scale and credit overview.
 *
 * One row = one Workspace (the sole tenant boundary). Everything shown is the
 * canonical backend data (`GET /platform/workspaces`, see
 * `tenant_usage_analytics_service` + `team_service`):
 *   - 团队状态 : `team_state` (personal / entitled_pending / team) — the Team
 *     ENTITLEMENT (capability) and the formed-team FACT are different things and
 *     are never inferred from each other here;
 *   - 授权方式 : `team_entitlement_sources` (积分达标 / 人为指定 / 套餐包含 / 未授权);
 *   - 成员数   : ACTIVE memberships only;
 *   - 积分使用 / 余额 : pooled Workspace credits, rolling 30 days used + balance.
 *
 * Kept from earlier tasks: server-side sort/filter/pagination with URL query
 * state (TASK-00005/00041 field-level sort contract), sticky table header
 * (TASK-00039: `SectionCard allowOverflow` + `<Table sticky>`; the filter bar
 * lives OUTSIDE that wrapper), canonical last-activity (TASK-00034).
 * Storage / 1·7·30 day usage / expiry live on Tenant Detail.
 * TASK-00667 — 「套餐」column: catalog display_name via plan_key (no hardcoded labels).
 */
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons'
import type { TableColumnsType, TablePaginationConfig } from 'antd'
import { Alert, Button, Dropdown, Form, Input, Modal, Select, Table, Tag, Tooltip, Typography, message } from 'antd'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { platformCreateWorkspace, platformListWorkspaces, platformSetTeamMode } from '../api'
import type { TeamState, TenantListQueryParams, TenantListSortField, TenantListSummary, WorkspaceWithCounts } from '../types'
import { SectionCard, SummaryCard, formatDateTime, formatRelativeTime } from './adminShared'
import { cardIconBg, platformColors } from './theme'
import CreditAdjustmentModal from './CreditAdjustmentModal'
import PlanUpgradeModal from './PlanUpgradeModal'
import TenantAccountActionModals from './TenantAccountActionModals'
import { sortDirectionsFor } from './tenantListSortContract'
import {
  ENTITLEMENT_SOURCE_LABELS,
  TEAM_STATE_LABELS,
  TEAM_STATE_TONES,
  entitlementBadges,
  formatPoints,
  manualEntitlementAction,
  tenantPlanCell,
  workspaceStatusLabel,
  workspaceStatusTone,
} from './tenantListTeamOverview'
import type { Tone } from './tenantListTeamOverview'

const RISK_LABELS: Record<string, string> = {
  disabled: '已停用',
  expired: '套餐已到期',
  expiring_soon: '套餐即将到期',
  negative_balance: '积分为负',
  storage_near_quota: '存储接近上限',
}

/** antd Tag colour for each presentation tone (purple/blue/green stay low-saturation presets). */
const TAG_COLOR: Record<Tone, string | undefined> = {
  green: 'green',
  purple: 'purple',
  blue: 'geekblue',
  gold: 'gold',
  red: 'red',
  default: undefined,
}

function RiskFlags({ flags }: { flags: string[] }) {
  if (!flags || flags.length === 0) return null
  return (
    <Tooltip title={flags.map((f) => RISK_LABELS[f] ?? f).join(' · ')}>
      <span style={{ color: platformColors.danger, marginLeft: 6, fontSize: 12 }}>⚠</span>
    </Tooltip>
  )
}

interface Filters {
  search: string
  status: string
  teamState: string
  entitlementSource: string
}

function filtersFromParams(params: URLSearchParams): Filters {
  return {
    search: params.get('search') || '',
    status: params.get('status') || '',
    teamState: params.get('team_state') || '',
    entitlementSource: params.get('entitlement_source') || '',
  }
}

const mutedSmall = { fontSize: 12, color: platformColors.mutedText } as const

export default function PlatformTenantsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const page = Number(searchParams.get('page') || '1') || 1
  const pageSize = Number(searchParams.get('page_size') || '10') || 10
  const sortBy = (searchParams.get('sort_by') || 'created_at') as TenantListSortField
  const sortDir = (searchParams.get('sort_dir') === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc'
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams])

  const [draftSearch, setDraftSearch] = useState(filters.search)
  useEffect(() => setDraftSearch(filters.search), [filters.search])

  const patchParams = useCallback(
    (patch: Record<string, string | null>, opts?: { resetPage?: boolean }) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === '') next.delete(k)
          else next.set(k, v)
        }
        if (opts?.resetPage !== false) next.delete('page')
        return next
      })
    },
    [setSearchParams],
  )

  const [rows, setRows] = useState<WorkspaceWithCounts[]>([])
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState<TenantListSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [createSaving, setCreateSaving] = useState(false)
  const [form] = Form.useForm<{
    name: string
    slug?: string
    owner_email: string
    owner_password: string
    owner_password_confirm: string
    owner_display_name?: string
  }>()
  const MIN_OWNER_PASSWORD = 8

  const [upgradeTarget, setUpgradeTarget] = useState<WorkspaceWithCounts | null>(null)
  const [adjustTarget, setAdjustTarget] = useState<WorkspaceWithCounts | null>(null)
  const [teamTarget, setTeamTarget] = useState<WorkspaceWithCounts | null>(null)
  const [teamForm] = Form.useForm<{ reason: string }>()
  const [teamSaving, setTeamSaving] = useState(false)
  const [accountTarget, setAccountTarget] = useState<WorkspaceWithCounts | null>(null)
  const [accountMode, setAccountMode] = useState<
    'detail' | 'reset_password' | 'force_logout' | 'disable' | 'enable' | 'delete_account' | 'delete_team' | null
  >(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    platformListWorkspaces({
      search: filters.search || undefined,
      status: filters.status || undefined,
      team_state: (filters.teamState as TeamState | '') || undefined,
      entitlement_source: (filters.entitlementSource as TenantListQueryParams['entitlement_source'] | '') || undefined,
      sort_by: sortBy,
      sort_dir: sortDir,
      page,
      page_size: pageSize,
    })
      .then((r) => {
        setRows(r.data.items)
        setTotal(r.data.total)
        setSummary(r.data.summary)
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [filters.search, filters.status, filters.teamState, filters.entitlementSource, sortBy, sortDir, page, pageSize])

  useEffect(load, [load])

  const onCreate = async () => {
    if (createSaving) return
    const values = await form.validateFields()
    setCreateSaving(true)
    try {
      await platformCreateWorkspace({
        name: values.name.trim(),
        slug: values.slug?.trim() || undefined,
        owner_email: values.owner_email.trim(),
        owner_password: values.owner_password,
        owner_display_name: values.owner_display_name?.trim() || undefined,
      })
      message.success(`租户「${values.name.trim()}」已创建，Owner 可使用邮箱登录`)
      setCreateOpen(false)
      form.resetFields()
      load()
    } catch (err) {
      const e = err as Error & { code?: string }
      if (e.code === 'owner_email_unavailable') {
        form.setFields([{ name: 'owner_email', errors: [e.message || '该邮箱不可用'] }])
      } else {
        message.error(e.message || '创建租户失败')
      }
    } finally {
      setCreateSaving(false)
    }
  }

  const closeCreateModal = () => {
    if (createSaving) return
    setCreateOpen(false)
    form.resetFields()
  }

  const teamAction = teamTarget ? manualEntitlementAction(teamTarget) : null
  const onConfirmTeamEntitlement = async () => {
    if (!teamTarget || !teamAction || teamAction.kind === 'none') return
    const { reason } = await teamForm.validateFields()
    setTeamSaving(true)
    try {
      await platformSetTeamMode(teamTarget.id, { enabled: teamAction.kind === 'grant', reason: reason.trim() })
      message.success(teamAction.kind === 'grant' ? '已授予人工团队授权' : '已撤销人工团队授权')
      setTeamTarget(null)
      teamForm.resetFields()
      load()
    } catch (err) {
      message.error((err as Error).message)
    } finally {
      setTeamSaving(false)
    }
  }

  const onTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<WorkspaceWithCounts> | SorterResult<WorkspaceWithCounts>[],
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter
    const patch: Record<string, string | null> = {
      page: String(pagination.current ?? 1),
      page_size: String(pagination.pageSize ?? 10),
    }
    if (s?.order && typeof s.columnKey === 'string') {
      patch.sort_by = s.columnKey
      patch.sort_dir = s.order === 'ascend' ? 'asc' : 'desc'
    }
    patchParams(patch, { resetPage: false })
  }

  const sortOrderFor = (field: TenantListSortField): 'ascend' | 'descend' | undefined =>
    sortBy === field ? (sortDir === 'asc' ? 'ascend' : 'descend') : undefined

  const columns: TableColumnsType<WorkspaceWithCounts> = [
    {
      title: '租户信息',
      key: 'name',
      align: 'left' as const,
      sorter: true,
      sortDirections: sortDirectionsFor('name'),
      sortOrder: sortOrderFor('name'),
      render: (_: unknown, row) => (
        <div style={{ minWidth: 180 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <a
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/platform/tenants/${row.id}`)
              }}
              style={{ fontWeight: 600 }}
            >
              {row.name}
            </a>
            <Tag
              style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '16px' }}
              color={row.team_state === 'team' ? 'green' : undefined}
            >
              {row.team_state === 'team' ? '团队' : '个人'}
            </Tag>
            <RiskFlags flags={row.risk_flags} />
          </div>
          <div style={mutedSmall}>
            <Tooltip title={`Workspace: ${row.slug}`}>
              <span>{row.owner_email ?? row.slug}</span>
            </Tooltip>
          </div>
        </div>
      ),
    },
    {
      title: '套餐',
      key: 'plan_key',
      width: 140,
      ellipsis: true,
      sorter: true,
      sortDirections: sortDirectionsFor('plan_key'),
      sortOrder: sortOrderFor('plan_key'),
      render: (_: unknown, row) => {
        const cell = tenantPlanCell(row)
        const tip =
          cell.kind === 'orphan'
            ? `plan_key=${row.plan_key}（目录中无对应套餐）`
            : cell.kind === 'missing'
              ? 'Workspace 未绑定 plan_key'
              : cell.kind === 'disabled'
                ? `${cell.label}（已停用）· plan_key=${row.plan_key}`
                : row.plan_key
                  ? `plan_key=${row.plan_key}`
                  : undefined
        return (
          <Tooltip title={tip}>
            <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', maxWidth: '100%' }}>
              <Tag
                color={TAG_COLOR.purple}
                style={{ marginInlineEnd: 0, maxWidth: 110 }}
                data-testid="tenant-list-plan"
              >
                <span
                  style={{
                    display: 'inline-block',
                    maxWidth: 96,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'bottom',
                  }}
                >
                  {cell.label}
                </span>
              </Tag>
              {cell.kind === 'disabled' ? (
                <Tag style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '16px' }}>已停用</Tag>
              ) : null}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: '团队状态',
      key: 'team_state',
      render: (_: unknown, row) => (
        <Tag color={TAG_COLOR[TEAM_STATE_TONES[row.team_state] ?? 'default']} style={{ marginInlineEnd: 0 }}>
          {TEAM_STATE_LABELS[row.team_state] ?? row.team_state}
        </Tag>
      ),
    },
    {
      title: '状态',
      key: 'status',
      sorter: true,
      sortDirections: sortDirectionsFor('status'),
      sortOrder: sortOrderFor('status'),
      render: (_: unknown, row) => (
        <Tag color={TAG_COLOR[workspaceStatusTone(row.status)]} style={{ marginInlineEnd: 0 }}>
          {workspaceStatusLabel(row.status)}
        </Tag>
      ),
    },
    {
      title: '授权方式',
      key: 'entitlement',
      render: (_: unknown, row) => (
        <Tooltip title={row.team_mode_enabled && row.team_enabled_at ? `授权时间：${formatDateTime(row.team_enabled_at)}` : undefined}>
          <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
            {entitlementBadges(row).map((b) => (
              <Tag key={b.key} color={TAG_COLOR[b.tone]} style={{ marginInlineEnd: 0 }}>
                {b.label}
              </Tag>
            ))}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '成员数',
      key: 'member_count',
      align: 'right' as const,
      sorter: true,
      sortDirections: sortDirectionsFor('member_count'),
      sortOrder: sortOrderFor('member_count'),
      render: (_: unknown, row) => (
        <Tooltip title="仅统计有效成员">
          <span style={{ fontWeight: 600 }}>{row.active_member_count}</span>
        </Tooltip>
      ),
    },
    {
      title: '积分使用 / 余额',
      key: 'credit_usage_30d',
      align: 'right' as const,
      sorter: true,
      sortDirections: sortDirectionsFor('credit_usage_30d'),
      sortOrder: sortOrderFor('credit_usage_30d'),
      render: (_: unknown, row) => (
        <Tooltip title="团队积分池 · 已用为近 30 天滚动统计">
          <div>
            <div style={{ fontWeight: 600 }}>{formatPoints(row.credit_usage_30d)}</div>
            <div style={{ ...mutedSmall, color: Number(row.credit_balance) < 0 ? platformColors.danger : platformColors.mutedText }}>
              余额 {formatPoints(row.credit_balance)}
            </div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: '最近活跃',
      key: 'recent_activity_at',
      sorter: true,
      sortDirections: sortDirectionsFor('recent_activity_at'),
      sortOrder: sortOrderFor('recent_activity_at'),
      render: (_: unknown, row) =>
        row.recent_activity_at ? (
          <Tooltip title={formatDateTime(row.recent_activity_at)}>
            <span>{formatRelativeTime(row.recent_activity_at)}</span>
          </Tooltip>
        ) : (
          <span style={{ color: platformColors.mutedText }}>暂无活动</span>
        ),
    },
    {
      title: '操作',
      key: 'actions',
      fixed: 'right' as const,
      render: (_: unknown, row: WorkspaceWithCounts) => {
        const action = manualEntitlementAction(row)
        // Canonical backend Owner status (TASK-00569) — never session-only optimism.
        const ownerStatus = row.owner_user_status === 'disabled' ? 'disabled' : 'active'
        const isTeam = row.team_state === 'team'
        return (
          <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
            <Button
              size="small"
              type="link"
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/platform/tenants/${row.id}`)
              }}
            >
              管理
            </Button>
            <Dropdown
              menu={{
                items: [
                  { type: 'group', label: '账号管理', children: [
                    { key: 'account_detail', label: '查看账号详情' },
                    { key: 'reset_password', label: '重置密码' },
                    { key: 'force_logout', label: '强制退出所有设备' },
                    {
                      key: ownerStatus === 'disabled' ? 'enable' : 'disable',
                      label: ownerStatus === 'disabled' ? '启用账号' : '停用账号',
                    },
                  ] },
                  { type: 'group', label: '租户管理', children: [
                    {
                      key: 'team',
                      label:
                        action.kind === 'grant'
                          ? '授予人工团队授权'
                          : action.kind === 'revoke'
                            ? '撤销人工团队授权'
                            : '团队功能资格（不可人工变更）',
                      disabled: action.kind === 'none',
                    },
                    { key: 'upgrade', label: '升级套餐', disabled: row.plan_key === 'premium' },
                    { key: 'adjust', label: '调整积分' },
                  ] },
                  { type: 'group', label: '危险操作', children: [
                    { key: 'delete_account', label: '删除账号', danger: true },
                    ...(isTeam ? [{ key: 'delete_team', label: '删除团队', danger: true }] : []),
                  ] },
                ],
                onClick: ({ key, domEvent }) => {
                  domEvent.stopPropagation()
                  if (key === 'team') {
                    teamForm.resetFields()
                    setTeamTarget(row)
                  }
                  if (key === 'upgrade') setUpgradeTarget(row)
                  if (key === 'adjust') setAdjustTarget(row)
                  if (
                    key === 'account_detail' ||
                    key === 'reset_password' ||
                    key === 'force_logout' ||
                    key === 'disable' ||
                    key === 'enable' ||
                    key === 'delete_account' ||
                    key === 'delete_team'
                  ) {
                    setAccountTarget(row)
                    setAccountMode(key === 'account_detail' ? 'detail' : key)
                  }
                },
              }}
              trigger={['click']}
            >
              <Button size="small" type="text" aria-label="更多操作" icon={<EllipsisOutlined />} onClick={(e) => e.stopPropagation()} />
            </Dropdown>
          </div>
        )
      },
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            租户管理
          </Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            管理租户、团队、成员与资源，并查看团队授权来源和使用情况。
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          创建租户
        </Button>
      </div>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      {/* KPI cards — all tenants (independent of the current filter/page), real aggregates only. */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <SummaryCard
          title="租户总数"
          value={summary ? formatPoints(summary.total_tenants) : '—'}
          hint={summary ? `正常 ${summary.active_tenants}` : undefined}
          iconBg={cardIconBg.tenant}
          icon="🏢"
          loading={!summary}
        />
        <SummaryCard
          title="团队总数"
          value={summary ? formatPoints(summary.team_total) : '—'}
          hint={summary ? `已获团队资格 ${summary.team_entitled_total}` : undefined}
          iconBg={cardIconBg.site}
          icon="👥"
          loading={!summary}
        />
        <SummaryCard
          title="成员总数"
          value={summary ? formatPoints(summary.member_total) : '—'}
          hint={summary ? '仅统计有效成员' : undefined}
          iconBg={cardIconBg.task}
          icon="🧑"
          loading={!summary}
        />
        <SummaryCard
          title="积分消耗（近30天）"
          value={summary ? formatPoints(summary.credit_usage_30d_total) : '—'}
          hint={summary ? '全部租户 · 滚动 30 天' : undefined}
          iconBg={cardIconBg.worker}
          icon="📊"
          loading={!summary}
        />
      </div>

      {/* Filter bar — deliberately NOT sticky (only the table header sticks). */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input.Search
          placeholder="搜索租户名称 / Workspace / 邮箱 / 团队名称…"
          allowClear
          style={{ width: 340 }}
          value={draftSearch}
          onChange={(e) => setDraftSearch(e.target.value)}
          onSearch={(v) => patchParams({ search: v || null })}
        />
        <Select
          allowClear
          placeholder="状态：全部"
          style={{ width: 130 }}
          value={filters.status || undefined}
          onChange={(v) => patchParams({ status: v || null })}
          options={[
            // Canonical `WorkspaceStatus` only has these two real values.
            { value: 'active', label: '正常' },
            { value: 'suspended', label: '已暂停' },
          ]}
        />
        <Select
          allowClear
          placeholder="团队状态：全部"
          style={{ width: 160 }}
          value={filters.teamState || undefined}
          onChange={(v) => patchParams({ team_state: v || null })}
          options={[
            { value: 'personal', label: TEAM_STATE_LABELS.personal },
            { value: 'entitled_pending', label: TEAM_STATE_LABELS.entitled_pending },
            { value: 'team', label: TEAM_STATE_LABELS.team },
          ]}
        />
        <Select
          allowClear
          placeholder="授权方式：全部"
          style={{ width: 160 }}
          value={filters.entitlementSource || undefined}
          onChange={(v) => patchParams({ entitlement_source: v || null })}
          options={[
            { value: 'credit_threshold', label: ENTITLEMENT_SOURCE_LABELS.credit_threshold },
            { value: 'platform_admin', label: ENTITLEMENT_SOURCE_LABELS.platform_admin },
            { value: 'plan', label: ENTITLEMENT_SOURCE_LABELS.plan },
            { value: 'none', label: ENTITLEMENT_SOURCE_LABELS.none },
          ]}
        />
        <Button
          onClick={() => {
            setDraftSearch('')
            setSearchParams(new URLSearchParams())
          }}
        >
          重置
        </Button>
      </div>

      <SectionCard title={`租户列表（共 ${total} 个租户）`} allowOverflow>
        <Table
          className="pf-data-table"
          rowKey="id"
          loading={loading}
          dataSource={rows}
          sticky
          scroll={{ x: true }}
          onRow={(row) => ({ onClick: () => navigate(`/platform/tenants/${row.id}`), style: { cursor: 'pointer' } })}
          onChange={onTableChange}
          columns={columns}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (t) => `共 ${t} 个租户`,
          }}
        />
      </SectionCard>

      {/* 授权方式说明 — concise, low visual weight (below the table). */}
      <div
        data-testid="entitlement-explainer"
        style={{
          marginTop: 12,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 12,
        }}
      >
        {[
          {
            key: 'credit_threshold',
            title: '积分达标授权',
            tag: <Tag color="purple">积分达标</Tag>,
            body: '当 Workspace 满足平台配置的团队资格积分条件后，系统自动授予团队功能资格。',
          },
          {
            key: 'platform_admin',
            title: '人为指定授权',
            tag: <Tag color="geekblue">人为指定</Tag>,
            body: '由 Platform Admin 为指定 Workspace 手动授予团队功能资格。',
          },
        ].map((c) => (
          <div
            key={c.key}
            style={{
              background: platformColors.surface,
              border: `1px solid ${platformColors.border}`,
              borderRadius: 10,
              padding: '10px 14px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13 }}>
              {c.tag}
              {c.title}
            </div>
            <div style={{ ...mutedSmall, marginTop: 4 }}>{c.body}</div>
          </div>
        ))}
        <div style={{ ...mutedSmall, gridColumn: '1 / -1' }}>
          团队功能资格不等于已组队：已获资格但仅有 1 名有效成员的租户显示为「已授权未组队」，有效成员 ≥ 2 即为「团队」。
        </div>
      </div>

      <Modal
        title="新建租户"
        open={createOpen}
        onOk={() => void onCreate()}
        onCancel={closeCreateModal}
        okText="创建"
        cancelText="取消"
        confirmLoading={createSaving}
        okButtonProps={{ disabled: createSaving }}
        maskClosable={!createSaving}
        destroyOnClose
        width={560}
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
        data-testid="platform-create-tenant-modal"
      >
        <Form form={form} layout="vertical" requiredMark autoComplete="off">
          <div style={{ fontWeight: 600, marginBottom: 8, color: platformColors.primaryText }}>租户信息</div>
          <Form.Item name="name" label="租户名称" rules={[{ required: true, whitespace: true, message: '请填写租户名称' }]}>
            <Input placeholder="e.g. LiangCollection" autoComplete="off" data-testid="platform-create-tenant-name" />
          </Form.Item>
          <Form.Item name="slug" label="Slug（可选）">
            <Input placeholder="留空则根据名称自动生成" autoComplete="off" data-testid="platform-create-tenant-slug" />
          </Form.Item>

          <div
            style={{
              fontWeight: 600,
              margin: '8px 0',
              paddingTop: 12,
              borderTop: `1px solid ${platformColors.border}`,
              color: platformColors.primaryText,
            }}
          >
            租户管理员（Owner）
          </div>
          <p style={{ ...mutedSmall, marginTop: 0, marginBottom: 12 }}>
            将同时创建一个可直接登录的 Owner 账号，并绑定到本租户。一个登录账号只能归属一个工作区。
          </p>
          <Form.Item
            name="owner_email"
            label="登录邮箱"
            rules={[
              { required: true, message: '请填写登录邮箱' },
              { type: 'email', message: '登录邮箱格式不正确' },
            ]}
          >
            <Input autoComplete="off" data-testid="platform-create-tenant-owner-email" />
          </Form.Item>
          <Form.Item name="owner_display_name" label="显示名称（可选）">
            <Input maxLength={100} autoComplete="off" placeholder="默认取邮箱 @ 前部分" data-testid="platform-create-tenant-owner-name" />
          </Form.Item>
          <Form.Item
            name="owner_password"
            label="初始密码"
            extra={`至少 ${MIN_OWNER_PASSWORD} 位，规则与租户端团队账号创建一致`}
            rules={[
              { required: true, message: '请填写初始密码' },
              { min: MIN_OWNER_PASSWORD, message: `密码至少 ${MIN_OWNER_PASSWORD} 位` },
            ]}
          >
            <Input.Password autoComplete="new-password" data-testid="platform-create-tenant-owner-password" />
          </Form.Item>
          <Form.Item
            name="owner_password_confirm"
            label="确认密码"
            dependencies={['owner_password']}
            rules={[
              { required: true, message: '请再次输入密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('owner_password') === value) return Promise.resolve()
                  return Promise.reject(new Error('两次输入的密码不一致'))
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" data-testid="platform-create-tenant-owner-password-confirm" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={teamAction?.kind === 'revoke' ? '撤销人工团队授权' : '授予人工团队授权'}
        open={!!teamTarget && teamAction?.kind !== 'none'}
        onOk={onConfirmTeamEntitlement}
        onCancel={() => setTeamTarget(null)}
        okText={teamAction?.kind === 'revoke' ? '确认撤销' : '确认授予'}
        okButtonProps={{ danger: teamAction?.kind === 'revoke', loading: teamSaving }}
        cancelText="取消"
        destroyOnClose
      >
        {teamTarget && (
          <div>
            <p style={{ marginTop: 0 }}>
              租户：<strong>{teamTarget.name}</strong>（{teamTarget.slug}）
            </p>
            <p>
              当前团队功能资格：
              {entitlementBadges(teamTarget).map((b) => (
                <Tag key={b.key} color={TAG_COLOR[b.tone]}>
                  {b.label}
                </Tag>
              ))}
              · 有效成员 {teamTarget.active_member_count} 人
            </p>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="仅变更团队功能资格；不会修改成员、积分余额、积分流水或任何历史数据。操作将记录审计日志。"
            />
            <Form form={teamForm} layout="vertical">
              <Form.Item
                name="reason"
                label="原因 / 来源说明"
                rules={[
                  { required: true, whitespace: true, message: '请填写原因，将写入审计日志' },
                  { max: 200, message: '最多 200 字' },
                ]}
              >
                <Input.TextArea rows={3} maxLength={200} placeholder="例如：试点客户，经运营确认开通团队功能" />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
      <Modal
        title="团队功能资格"
        open={!!teamTarget && teamAction?.kind === 'none'}
        onCancel={() => setTeamTarget(null)}
        footer={<Button onClick={() => setTeamTarget(null)}>知道了</Button>}
      >
        {teamAction?.kind === 'none' ? teamAction.reason : null}
      </Modal>

      {upgradeTarget && (
        <PlanUpgradeModal
          open
          onClose={() => setUpgradeTarget(null)}
          workspaceId={upgradeTarget.id}
          workspaceName={upgradeTarget.name}
          currentPlanKey={upgradeTarget.plan_key}
          currentPlanDisplayName={upgradeTarget.plan_display_name}
          creditBalance={upgradeTarget.credit_balance}
          storageQuotaBytes={upgradeTarget.storage_quota_bytes}
          onSuccess={load}
        />
      )}
      {adjustTarget && (
        <CreditAdjustmentModal
          open
          onClose={() => setAdjustTarget(null)}
          workspaceId={adjustTarget.id}
          workspaceName={adjustTarget.name}
          creditBalance={adjustTarget.credit_balance}
          onSuccess={load}
        />
      )}

      <TenantAccountActionModals
        workspace={accountTarget}
        mode={accountMode}
        onClose={() => {
          setAccountTarget(null)
          setAccountMode(null)
        }}
        onSuccess={() => {
          load()
        }}
      />
    </div>
  )
}
